<template>
  <div :class="classes" v-if="productTypes">
    <div class="calculator__mobile-toggle" v-if="isMobile && isReduced">
      <BasicButton
        native-type="button"
        buttonType="filled"
        v-if="!mobileToggled"
        class="calculator__mobile-toggle-open"
        :label="'Preis neu berechnen'"
        @button-click="mobileToggled = true"
      ></BasicButton>

      <IconButton
        v-if="mobileToggled"
        type="filled"
        class="calculator__mobile-toggle-close"
        iconName="close"
        @click="mobileToggled = false"
      />
    </div>

    <CalculatorSegmentedButtons
      role="tablist"
      aria-label="Wählen Sie die Energieart"
      :options="segmentedButtonsOptions"
      :selectedValue="selectedCalculatorType"
      :uid="uid"
      @update:selectedValue="handleUpdateCalculatorType"
      v-if="requiresTabs && !isReduced"
    />

    <component
      v-if="
        (isReduced && (!isMobile || (mobileToggled && isMobile))) || !isReduced
      "
      v-for="product in mappedProducts"
      :key="`${uid}-${product.type}`"
      :is="product.component"
      v-bind="props"
      v-show="selectedCalculatorType === product.type"
      :is-shown="selectedCalculatorType === product.type"
      :component-id="props.componentId"
      :redirect-page="product.redirectPage"
      :badge="product.badge"
      :headline="CALC_LANG.services[product.typeReadable]"
      :calculator-type="product.type"
      :variant="product.typeReadable"
      :id="`${requiresTabs ? `${uid}-tabpanel-${product.type}` : ''}`"
      :aria-labelledby="`${requiresTabs ? `${uid}-tab-${product.type}` : ''}`"
      :role="requiresTabs ? 'tabpanel' : undefined"
      :reduced="isReduced"
    />
  </div>
</template>

<script setup>
import '@material/web/iconbutton/filled-icon-button';
import '@material/web/radio/radio';
import IconButton from 'ewi-ui/components/2-molecules/IconButton';
import BasicButton from 'ewi-ui/components/1-atoms/BasicButton';
import { CALC_LANG } from './translations.js';
import CalculatorSegmentedButtons from 'ewi-ui/components/1-atoms/CalculatorSegmentedButtons/CalculatorSegmentedButtons.vue';

import CalculatorPower from './variants/CalculatorPower.vue';
import CalculatorGas from './variants/CalculatorGas.vue';
import CalculatorHeatflow from './variants/CalculatorHeatflow.vue';
import { CALCULATOR_TYPE } from './helpers.js';

import './Calculator.scss';
import { computed, ref, onBeforeMount } from 'vue';
import { calculatorStore } from './_store/calculator';
import { throttle } from 'ewi-ui-helpers/events';
import { v4 as uuidv4 } from 'uuid';

const props = defineProps({
  productTypes: Object,
  customerOptions: {
    type: String,
    default: '',
  },
  componentId: String,
  width: {
    type: String,
    default: 'half',
  },
  theme: String,
  promotionCode: String,
  disableVouchers: String,
  onlyThisPage: String,
  reduced: {
    type: String,
    default: null,
  },
});

const store = calculatorStore();
const selectedCalculatorType = ref(CALCULATOR_TYPE.POWER);
const mobileToggled = ref(false);
const isReduced = ref(props.reduced === '1');
const isMobile = ref(window.innerWidth <= 768);

const uid = uuidv4();

const handleResize = () => {
  isMobile.value = window.innerWidth <= 768;
};

const classes = computed(() => ({
  calculator: true,
  [`calculator--${props.width}`]: props.width,
  [`theme-${props.theme}`]: props.theme,
  ['calculator--reduced']: isReduced.value,
}));

const mappedProducts = computed(() => {
  const componentMap = {
    power: CalculatorPower,
    gas: CalculatorGas,
    heatflow: CalculatorHeatflow,
  };

  return Object.values(props.productTypes).map((product) => ({
    ...product,
    component: product.typeReadable
      ? product.typeReadable === 'dynamic_strom'
        ? componentMap.power
        : componentMap[product.typeReadable]
      : null,
  }));
});

const segmentedButtonsOptions = computed(() =>
  Object.values(props.productTypes).map((product) => ({
    label: CALC_LANG.services[product.typeReadable],
    value: product.type,
  })),
);

const requiresTabs = computed(
  () =>
    props.productTypes &&
    Object.values(props.productTypes).filter(
      (product) => product.typeReadable != '',
    ).length > 1,
);

const handleUpdateCalculatorType = (value) => {
  selectedCalculatorType.value = value;
};

const productTypesHasType = (productTypes, type) => {
  return (
    Object.values(productTypes).find((product) => product.type === type) !==
    undefined
  );
};

const getFirstPreselectedCalculator = () => {
  const { calculatorType, customerType } = store.getLastUsedCalculator() || {
    calculatorType: null,
    customerType: null,
  };
  if (
    calculatorType !== null &&
    productTypesHasType(props.productTypes, calculatorType)
  ) {
    return { type: calculatorType, customerType };
  }
  return Object.values(props.productTypes).find(
    (product) => product.isSelected === true,
  );
};

onBeforeMount(() => {
  window.addEventListener('resize', throttle(handleResize, 300));
  const preselectedCalculator =
    Object.values(props.productTypes).length > 1
      ? getFirstPreselectedCalculator()
      : Object.values(props.productTypes)[0];
  if (preselectedCalculator) {
    selectedCalculatorType.value = preselectedCalculator.type;
  }
});
</script>
